import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";

import { tokens } from "../../theme";
import useDebounce from "../../hooks/useDebounce";
import { getUnallocatedPayments } from "../../redux/actions/unallocatedPaymentActions";
import StandardTable from "../common/StandardTable";
import DateRangeFilter from "../common/DateRangeFilter";

const AllocatePaymentModal = ({ open, onClose, onSelect }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    unallocatedPayments,
    loadingUnallocated,
    errorLoadingUnallocated,
    pagination,
  } = useSelector((state) => state.unallocatedPayment);

  const today = new Date().toISOString().split("T")[0];
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchPayments = useCallback(() => {
    dispatch(
      getUnallocatedPayments({
        page,
        size: rowsPerPage,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, page, rowsPerPage, debouncedSearch, startDate, endDate]);

  useEffect(() => {
    if (open) fetchPayments();
  }, [open, fetchPayments]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  const columns = [
    {
      id: "createdAt",
      label: "Date",
      format: (val) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {new Date(val).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(val).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Box>
      ),
    },
    {
      id: "gatewayReceiptNumber",
      label: "Reference",
      format: (val) => (
        <Chip
          label={val}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha(theme.palette.secondary.main, 0.1),
            color: theme.palette.secondary.main,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
          }}
        />
      ),
    },
    {
      id: "customerName",
      label: "Customer",
      format: (_, row) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {row.customerName || "N/A"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.customerPhone || "No Phone"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      align: "right",
      format: (val, row) => (
        <Typography variant="body2" fontWeight={700} color="secondary.main">
          {row.currency} {Number(val).toFixed(2)}
        </Typography>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: colors.primary[400],
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
        Allocate Electronic Payment
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 1 : 3 }}>
        {/* --- Highlighted Filter Box --- */}
        <Box
          sx={{
            mb: 3,
            p: 2,
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            alignItems={isMobile ? "stretch" : "center"}
            justifyContent="space-between"
          >
            <TextField
              label="Search Ref / Phone"
              size="small"
              sx={{
                width: isMobile ? "100%" : 320,
                "& .MuiOutlinedInput-root": { bgcolor: colors.primary[400] },
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by reference or phone..."
            />

            <DateRangeFilter
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Stack>
        </Box>

        {/* --- Standard Table with explicit container styles --- */}
        <Box>
          <StandardTable
            columns={columns}
            data={unallocatedPayments}
            loading={loadingUnallocated}
            error={errorLoadingUnallocated}
            onRetry={fetchPayments}
            page={page}
            count={pagination?.totalElements || 0}
            rowsPerPage={rowsPerPage}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(newSize) => {
              setRowsPerPage(newSize);
              setPage(1);
            }}
            rowAction={(row) => (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: "bold", textTransform: "none" }}
                onClick={() => onSelect(row)}
              >
                Link
              </Button>
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: `1px solid ${colors.primary[300]}` }}
      >
        <Button onClick={onClose} sx={{ color: colors.grey[100] }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllocatePaymentModal;
